import { useState, useMemo } from "react";
import Select from "react-select";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { AgGridReact } from "ag-grid-react";
import { PageTable, Header } from "/src/layouts/page";
import InputSearch from "/src/components/UI/InputSearch/InputSearch";
import ActionTypeBadge from "../ActionTypeBadge/ActionTypeBadge";
import LogMessage from "../LogMessage/LogMessage";
import UserCell from "../UserCell/UserCell";
import logService from "../../services/logService";
import { formatDateWithoutSeconds } from "/src/utils/dateWithoutSeconds";
import capitalizeFirstLetter from "/src/utils/capitalizeFirstLetter";
import "./LogsTable.css";

const ACTION_TYPE_OPTIONS = [
    { value: "create", label: "Create" },
    { value: "update", label: "Update" },
    { value: "delete", label: "Delete" },
    { value: "warning", label: "Warning" },
];

function LogsTable() {
    const { getLogs } = logService;
    const [isLoading, setIsLoading] = useState(true);
    const [gridApi, setGridApi] = useState(null);
    const [filters, setFilters] = useState({
        email: "",
        search: "",
        action_types: [],
        date_start: "",
        date_end: "",
    });

    const datasource = useMemo(() => ({
        getRows: async (params) => {
            setIsLoading(true);
            const startRow = params.startRow;
            const endRow = params.endRow;
            const limit = endRow - startRow;
            const page = Math.floor(startRow / limit) + 1;

            const cleanedFilters = Object.fromEntries(
                Object.entries(filters).filter(
                    ([_, value]) => value !== "" && value !== null && !(Array.isArray(value) && value.length === 0)
                )
            );

            try {
                const [response] = await getLogs({ page, limit, ...cleanedFilters });
                params.successCallback(response.data || [], response.total || 0);
            } catch (e) {
                console.error(e);
                params.failCallback();
            }
            setIsLoading(false);
        }
    }), [getLogs, filters]);

    const columnDefs = useMemo(() => [
        {
            headerName: "Date",
            field: "created_at",
            width: 170,
            sortable: true,
            valueFormatter: (params) => formatDateWithoutSeconds(params.value, false),
        },
        {
            headerName: "User",
            field: "user_email",
            width: 200,
            cellRenderer: (params) => <UserCell email={params.value} />,
        },
        {
            headerName: "Action",
            field: "action_type",
            width: 110,
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            cellRenderer: (params) => <ActionTypeBadge value={params.value} />,
        },
        {
            headerName: "Entity",
            field: "entity_type",
            width: 160,
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(params.value.replace(/_/g, " ")) : "—"),
        },
        {
            headerName: "Message",
            field: "message",
            flex: 1,
            minWidth: 350,
            wrapText: true,
            cellStyle: { display: "flex", alignItems: "center", lineHeight: "1.4", paddingTop: "8px", paddingBottom: "8px" },
            cellRenderer: (params) => <LogMessage message={params.value} />,
        },
    ], []);

    return (
        <PageTable>
            <Header title="Platform activity logs" />
            <br></br>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <InputSearch
                    name="email"
                    placeholderLibelle="Search by user email"
                    style={{ width: "220px", marginBottom: "10px" }}
                    onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
                />
                <InputSearch
                    name="search"
                    placeholderLibelle="Search in message"
                    style={{ width: "260px", marginBottom: "10px" }}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
                <Select
                    classNamePrefix="criteria-select"
                    isMulti
                    options={ACTION_TYPE_OPTIONS}
                    value={ACTION_TYPE_OPTIONS.filter(opt => filters.action_types.includes(opt.value))}
                    onChange={(selected) => setFilters(prev => ({
                        ...prev,
                        action_types: selected ? selected.map(s => s.value) : []
                    }))}
                    hideSelectedOptions={false}
                    controlShouldRenderValue={false}
                    closeMenuOnSelect={false}
                    placeholder={
                        filters.action_types.length
                            ? `${filters.action_types.length} action type${filters.action_types.length > 1 ? "s" : ""} selected`
                            : "Filter by action type..."
                    }
                    menuPortalTarget={document.body}
                    styles={{
                        container: (base) => ({ ...base, width: "260px", marginBottom: "10px" }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                />
                <div className="container_dates">
                    <Tippy
                        className="date-input-tooltip"
                        content={
                            <div className="date-input-text-tooltip">
                                Date Start
                            </div>
                        }
                        placement="top"
                        delay={[200, 0]}
                    >
                        <input
                            type="date"
                            className="input_search"
                            value={filters.date_start}
                            onChange={(e) => setFilters(prev => ({ ...prev, date_start: e.target.value }))}
                        />
                    </Tippy>

                    <Tippy
                        className="date-input-tooltip"
                        content={
                            <div className="date-input-text-tooltip">
                                Date End
                            </div>
                        }
                        placement="top"
                        delay={[200, 0]}
                    >
                        <input
                            type="date"
                            className="input_search"
                            value={filters.date_end}
                            onChange={(e) => setFilters(prev => ({ ...prev, date_end: e.target.value }))}
                        />
                    </Tippy>
                </div>
            </div>
            <br></br>
            <div className="logs-table">
                <div className="ag-theme-alpine" style={{ width: "100%", height: "650px" }}>
                    <AgGridReact
                        enableCellTextSelection={true}
                        rowHeight={72}
                        rowModelType="infinite"
                        datasource={datasource}
                        cacheBlockSize={30}
                        pagination={true}
                        paginationPageSize={30}
                        columnDefs={columnDefs}
                        defaultColDef={{ sortable: true, filter: true, resizable: true, cellStyle: { display: "flex", alignItems: "center" } }}
                        loading={isLoading}
                        onGridReady={(params) => setGridApi(params.api)}
                        getRowStyle={(params) => {
                            if (!params.data) return { opacity: 0 };
                        }}
                    />
                </div>
            </div>
        </PageTable>
    );
}

export default LogsTable;
